import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import {
  useFragment as useApolloFragment,
  useMutation,
  useQuery,
} from '@apollo/client/react';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  Pagination,
  PaginationItem,
  Skeleton,
  Toolbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE,
  type ExternalTransactionBody,
  type TextArrayLiteral,
  encodeTextArray,
} from '@realadvisor/common-types';
import { ArrowLeftIcon, ArrowRightIcon, LayoutGrid, Rows3 } from 'lucide-react';

import {
  type Cma_Reports_Property_Transactions_Insert_Input,
  type Property_Transactions_Distance_Bool_Exp,
  type Property_Transactions_Distance_Order_By,
} from '@graphql/__generated__/graphql';
import type { QuickFilterDefinition } from '@modules/_shared/components/filters/Filters';
import { ListToolbar } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { useReportContext } from '@modules/_shared/components/report-editor/ReportEditorContext';
import { SlideUpTransition } from '@modules/_shared/components/SlideDialog';
import { usePropertyTypeOptionRenderer } from '@modules/_shared/hooks/usePropertyTypeOptionRenderer';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useScopedSearchParams } from '@modules/_shared/utils/navigation';
import { getPaginationVariables } from '@modules/_shared/utils/paginations';
import {
  CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY,
  CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY,
  CMA_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY,
  CMA_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY,
  GET_PLACE_GEOMETRIES_QUERY,
  REPLACE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
  STEP_TRANSACTIONS_FRAGMENT,
} from '@modules/reports/cma-reports/cmaReportsQueries';
import { getEligibleExternalTransactionSource } from '@modules/reports/cma-reports/step-transaction/externalTransactionSources';
import { TransactionGridView } from '@modules/reports/cma-reports/step-transaction/TransactionGridView';
import { TransactionListView } from '@modules/reports/cma-reports/step-transaction/TransactionListView';
import type {
  CmaPropertyTransaction,
  CrmManualSelectionRow,
  ExternalManualSelectionRow,
  MergedTransactionRow,
  TransactionSource,
} from '@modules/reports/cma-reports/step-transaction/types';
import { dedupeManualSelectionRows } from '@modules/reports/cma-reports/step-transaction/utils/dedupeRows';
import {
  hasUnsupportedExternalTransactionFilters,
  hasUnsupportedExternalTransactionLocalities,
} from '@modules/reports/cma-reports/step-transaction/utils/externalFilters';
import { getLocalitySearchRadiusMeters } from '@modules/reports/cma-reports/step-transaction/utils/localitySearchRadius';
import {
  type OrderByObject,
  getExternalPrimarySort,
  mergeManualSelectionRows,
} from '@modules/reports/cma-reports/step-transaction/utils/mergeRows';
import {
  decodeSelectionKey,
  encodeSelectionKey,
} from '@modules/reports/cma-reports/step-transaction/utils/selectionKey';
import { refetchTransactionsUnlessReturned } from '@modules/reports/cma-reports/step-transaction/utils/transactionsRefetch';
import { SelectionPickerHeader } from '@modules/reports/shared/SelectionPickerHeader';
import { useAnalytics } from '@shared/analytics';
import { ANALYTICS_EVENTS } from '@shared/analytics/constants';
import { getBaseCmaEventProperties } from '@shared/analytics/posthog-helpers';
import { useLocale } from '@shared/hooks/locale';

const PaginationPreviousIcon = () => <ArrowLeftIcon size={20} />;
const PaginationNextIcon = () => <ArrowRightIcon size={20} />;

type LocalityPlaceData = {
  id: string;
  children_bbox: unknown;
  simplified_geom?: unknown;
};

type ExternalSearchRow = ExternalTransactionBody & {
  distance_in_meters?: number | null;
};

const parseJsonParam = <T,>(raw: string, defaultValue: T): T => {
  try {
    return raw ? (JSON.parse(raw) as T) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const savedKey = (tx: CmaPropertyTransaction): string | null => {
  if (tx.source === 'crm') {
    if (!tx.transaction_id) {
      console.error(`CRM transaction row ${tx.id} missing transaction_id`);
      return null;
    }
    return encodeSelectionKey('crm', tx.transaction_id);
  }
  if (tx.external_id == null) {
    console.error(`External transaction row ${tx.id} missing external_id`);
    return null;
  }
  return encodeSelectionKey(tx.source, tx.external_id);
};

type TransactionManualSelectionDialogProps = {
  open: boolean;
  onClose: () => void;
  cmaReportId: string;
  existingTransactions?: CmaPropertyTransaction[];
};

const TransactionManualSelectionDialog = ({
  open,
  onClose,
  cmaReportId,
  existingTransactions = [],
}: TransactionManualSelectionDialogProps) => {
  const theme = useTheme();
  const { data: cmaReportData } = useReportContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useLocale();
  const { me } = useAppData();
  const { track } = useAnalytics();
  const isEsTenant = me?.tenant?.country_code === 'ES';
  const cmaReport =
    cmaReportData?.__typename === 'cma_reports' ? cmaReportData : null;
  const transactionsFragment = useApolloFragment({
    fragment: STEP_TRANSACTIONS_FRAGMENT,
    fragmentName: 'StepTransactions',
    from: { __typename: 'cma_reports', id: cmaReportId },
  });
  const property = transactionsFragment.complete
    ? transactionsFragment.data.property
    : null;
  const externalSourceAdapter = getEligibleExternalTransactionSource({
    countryCode: property?.country_code,
    lat: property?.lat,
    lng: property?.lng,
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [searchParams, setSearchParams] = useScopedSearchParams(
    'transaction-selection',
  );

  const creationSource = cmaReport?.report_source ?? null;

  const existingKeys = useMemo(
    () =>
      new Set(
        existingTransactions
          .map(savedKey)
          .filter((k): k is string => k != null),
      ),
    [existingTransactions],
  );

  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenRef.current) {
      setSelectedIds(new Set(existingKeys));
    }
    prevOpenRef.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const whereRaw = searchParams.get('where') ?? '';
  const orderRaw = searchParams.get('order_by') ?? '';
  const where = useMemo<Property_Transactions_Distance_Bool_Exp>(
    () => parseJsonParam<Property_Transactions_Distance_Bool_Exp>(whereRaw, {}),
    [whereRaw],
  );
  const orderBy = useMemo<Property_Transactions_Distance_Order_By[]>(
    () =>
      parseJsonParam(orderRaw, [
        { distance_in_meters: 'asc_nulls_last' },
      ] as Property_Transactions_Distance_Order_By[]),
    [orderRaw],
  );

  const hasWhereParam = whereRaw.length > 0;
  const hasLocationFilter = Boolean(where?.transaction?.locality_place);
  const hasUnsupportedExternalFilters =
    hasUnsupportedExternalTransactionFilters(where);

  const renderPropertyType = usePropertyTypeOptionRenderer();

  const quickFilters = useMemo<QuickFilterDefinition[]>(
    () => [
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'property_main_type',
          '_in',
        ],
        label: t('Property type'),
        filterAutocompleteRenderOption: renderPropertyType,
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'locality_place',
        ],
        label: t('Location'),
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'signature_date',
        ],
        label: t('Signature Date'),
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'living_surface',
        ],
        label: t('Living surface'),
        shouldDisplay: () => !isEsTenant,
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'built_surface',
        ],
        label: t('Built surface'),
        shouldDisplay: () => isEsTenant,
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'land_surface',
        ],
        label: t('Land surface'),
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'number_of_rooms',
        ],
        label: t('Rooms'),
        shouldDisplay: () => !isEsTenant,
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'number_of_bedrooms',
        ],
        label: t('Rooms'),
        shouldDisplay: () => isEsTenant,
      },
      {
        path: [
          'property_transactions_distance_bool_exp',
          'transaction',
          'purchase_price',
        ],
        label: t('Price'),
      },
    ],
    [renderPropertyType, t, isEsTenant],
  );

  const { limit, offset } = getPaginationVariables(searchParams);
  const currentPage = Math.floor(offset / Math.max(limit, 1)) + 1;
  const viewType = searchParams.get('view') ?? 'list';

  useEffect(() => {
    const currentOffset = Number(searchParams.get('offset') ?? '0');
    if (currentOffset === 0) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set('offset', '0');
    setSearchParams(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [where, orderBy]);

  const localityPlaceIds = useMemo<string[]>(() => {
    const localityPlaceIdExp = where?.transaction?.locality_place?.id;
    const inList = localityPlaceIdExp?._in;
    const eqVal = localityPlaceIdExp?._eq;
    if (Array.isArray(inList) && inList.length > 0) {
      return inList;
    }
    if (typeof eqVal === 'string') {
      return [eqVal];
    }
    return [];
  }, [where?.transaction?.locality_place]);

  const withLocalityGeometry =
    externalSourceAdapter != null && localityPlaceIds.length === 1;
  const {
    data: localityPlaceData,
    loading: localityPlaceLoading,
    error: localityPlaceError,
  } = useQuery(GET_PLACE_GEOMETRIES_QUERY, {
    variables: { ids: localityPlaceIds, withGeometry: withLocalityGeometry },
    skip: localityPlaceIds.length === 0,
  });

  const localityPlaces = useMemo<LocalityPlaceData[]>(
    () => (localityPlaceData?.places as LocalityPlaceData[] | undefined) ?? [],
    [localityPlaceData?.places],
  );

  const localityPolygonGeoJson = useMemo(() => {
    if (!withLocalityGeometry) {
      return null;
    }
    const polygons: unknown[] = [];
    for (const p of localityPlaces) {
      const geom = p.simplified_geom as
        | { type?: string; coordinates?: unknown[] }
        | null
        | undefined;
      if (geom?.type === 'MultiPolygon' && Array.isArray(geom.coordinates)) {
        polygons.push(...geom.coordinates);
      } else if (geom?.type === 'Polygon' && Array.isArray(geom.coordinates)) {
        polygons.push(geom.coordinates);
      } else if (geom != null) {
        console.warn('Skipping unsupported locality geometry', p.id);
      }
    }
    if (polygons.length === 0) {
      return null;
    }
    const json = JSON.stringify({
      type: 'MultiPolygon',
      coordinates: polygons,
    });
    if (json.length > 65536) {
      console.warn(
        'Skipping DVF transactions because the locality geometry is too large',
        { localityPlaceIds },
      );
      return null;
    }
    return json;
  }, [withLocalityGeometry, localityPlaceIds, localityPlaces]);

  const externalVariables = useMemo(() => {
    const propertyMainTypes =
      where?.transaction?.property_main_type?._in ?? null;
    const primarySort = getExternalPrimarySort(orderBy);

    const cutoff = where?.transaction?.signature_date?._gte;
    const cutoffDate = typeof cutoff === 'string' ? cutoff.slice(0, 10) : null;
    const cutoffMax = where?.transaction?.signature_date?._lte;
    const cutoffDateMax =
      typeof cutoffMax === 'string' ? cutoffMax.slice(0, 10) : null;

    const livingSurface = where?.transaction?.living_surface;
    const landSurface = where?.transaction?.land_surface;
    const rooms = where?.transaction?.number_of_rooms;
    const price = where?.transaction?.purchase_price;
    const postcode = where?.transaction?.postcode?._eq;

    return {
      lat: property?.lat ?? null,
      lng: property?.lng ?? null,
      source: encodeTextArray(
        externalSourceAdapter ? [externalSourceAdapter.source] : null,
      ),
      max_results: EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE,
      property_main_type: encodeTextArray(propertyMainTypes),
      cutoff_date: cutoffDate,
      cutoff_date_max: cutoffDateMax,
      postcode: typeof postcode === 'string' ? postcode : null,
      polygon_geojson: localityPolygonGeoJson,
      living_surface_min: livingSurface?._gte ?? null,
      living_surface_max: livingSurface?._lte ?? null,
      land_surface_min: landSurface?._gte ?? null,
      land_surface_max: landSurface?._lte ?? null,
      rooms_min: rooms?._gte ?? null,
      rooms_max: rooms?._lte ?? null,
      price_min: price?._gte ?? null,
      price_max: price?._lte ?? null,
      sort_field: primarySort?.field ?? null,
      sort_direction: primarySort?.direction ?? null,
    };
  }, [
    property?.lat,
    property?.lng,
    externalSourceAdapter,
    where?.transaction?.property_main_type,
    where?.transaction?.signature_date,
    where?.transaction?.living_surface,
    where?.transaction?.land_surface,
    where?.transaction?.number_of_rooms,
    where?.transaction?.purchase_price,
    where?.transaction?.postcode,
    localityPolygonGeoJson,
    orderBy,
  ]);

  const [saveTransactions, { loading: upsertingTransactions }] = useMutation(
    REPLACE_CMA_REPORTS_PROPERTY_TRANSACTIONS,
  );

  const bboxMatchesCurrentIds = useMemo(() => {
    if (localityPlaceIds.length === 0) {
      return true;
    }
    const loadedIds = new Set(localityPlaces.map(p => p.id));
    return localityPlaceIds.every(id => loadedIds.has(id));
  }, [localityPlaceIds, localityPlaces]);
  const localitySearchRadius = useMemo(
    () =>
      bboxMatchesCurrentIds
        ? getLocalitySearchRadiusMeters(
            localityPlaces,
            property?.lat ?? null,
            property?.lng ?? null,
          )
        : null,
    [bboxMatchesCurrentIds, localityPlaces, property?.lat, property?.lng],
  );
  const waitingForLocalityBounds =
    localityPlaceIds.length > 0 && localityPlaceLoading;

  const skipQueries =
    !open ||
    upsertingTransactions ||
    !hasLocationFilter ||
    !property?.id ||
    waitingForLocalityBounds;

  const hasExternalSpatialPredicate = localityPolygonGeoJson != null;
  const externalMultiLocalityUnavailable =
    externalSourceAdapter != null &&
    hasUnsupportedExternalTransactionLocalities(localityPlaceIds);
  const externalGeometryUnavailable =
    withLocalityGeometry &&
    bboxMatchesCurrentIds &&
    !hasExternalSpatialPredicate;

  const skipExternalQuery =
    skipQueries ||
    externalSourceAdapter == null ||
    externalVariables.lat == null ||
    externalVariables.lng == null ||
    externalVariables.source == null ||
    hasUnsupportedExternalFilters ||
    externalMultiLocalityUnavailable ||
    !bboxMatchesCurrentIds ||
    !hasExternalSpatialPredicate;

  const externalLat = externalVariables.lat as number;
  const externalLng = externalVariables.lng as number;
  const externalSource = externalVariables.source as TextArrayLiteral;

  const maxSignatureDate = new Date().toISOString().slice(0, 10);
  const effectiveExternalCutoffDateMax =
    externalVariables.cutoff_date_max == null ||
    externalVariables.cutoff_date_max > maxSignatureDate
      ? maxSignatureDate
      : externalVariables.cutoff_date_max;

  const {
    data: externalSearchData,
    loading: externalSearchLoading,
    error: externalSearchError,
    refetch: refetchExternal,
  } = useQuery(CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY, {
    variables: {
      ...externalVariables,
      max_distance_meters: null,
      lat: externalLat,
      lng: externalLng,
      cutoff_date_max: effectiveExternalCutoffDateMax,
      source: externalSource,
    },
    skip: skipExternalQuery,
    context: { clientName: 'scrapers' },
  });

  const {
    max_results: _ignoredCountMax,
    sort_field: _ignoredCountSortField,
    sort_direction: _ignoredCountSortDirection,
    ...externalCountVariables
  } = externalVariables;
  const {
    data: externalSearchCountData,
    loading: externalSearchCountLoading,
    error: externalSearchCountError,
  } = useQuery(CMA_EXTERNAL_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY, {
    variables: {
      ...externalCountVariables,
      max_distance_meters: null,
      lat: externalLat,
      lng: externalLng,
      cutoff_date_max: effectiveExternalCutoffDateMax,
      source: externalSource,
    },
    skip: skipExternalQuery,
    context: { clientName: 'scrapers' },
  });

  const localExternalRows = useMemo(
    () =>
      externalSearchData?.fr_get_external_transactions_distance?.filter(
        row =>
          row.signature_date == null ||
          row.signature_date.slice(0, 10) <= maxSignatureDate,
      ) ?? [],
    [
      externalSearchData?.fr_get_external_transactions_distance,
      maxSignatureDate,
    ],
  );
  const externalResultsTruncated =
    (externalSearchCountData?.fr_get_external_transactions_distance_aggregate
      ?.aggregate?.count ?? 0) > EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE;
  const mergeWithExternal = !skipExternalQuery && externalSearchError == null;

  const rowsVariables = useMemo(() => {
    return {
      target_property_id: property?.id ?? '',
      max_distance_meters: localitySearchRadius,
      order_by: orderBy,
      where,
      limit: mergeWithExternal
        ? (EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE as number | null)
        : (Math.max(limit, 1) as number | null),
      offset: mergeWithExternal ? null : (offset as number | null),
    };
  }, [
    property?.id,
    localitySearchRadius,
    orderBy,
    where,
    mergeWithExternal,
    limit,
    offset,
  ]);

  const {
    data: searchData,
    dataState: searchDataState,
    loading: searchLoading,
    error: searchError,
    refetch: refetchSearch,
  } = useQuery(CMA_TRANSACTIONS_WITH_DISTANCE_ROWS_QUERY, {
    variables: rowsVariables,
    skip: skipQueries,
    notifyOnNetworkStatusChange: true,
    returnPartialData: true,
  });

  const {
    data: searchCountData,
    error: searchCountError,
    refetch: refetchSearchCount,
  } = useQuery(CMA_TRANSACTIONS_WITH_DISTANCE_COUNT_QUERY, {
    variables: {
      target_property_id: rowsVariables.target_property_id,
      max_distance_meters: rowsVariables.max_distance_meters,
      where: rowsVariables.where,
    },
    skip: skipQueries,
  });
  const crmCount =
    searchCountData?.get_property_transactions_distance_aggregate?.aggregate
      ?.count ?? 0;
  const crmResultsTruncated =
    mergeWithExternal &&
    crmCount > EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE;

  const externalQueryError =
    !skipQueries && externalSourceAdapter != null
      ? (localityPlaceIds.length === 1 && !hasUnsupportedExternalFilters
          ? localityPlaceError
          : undefined) ??
        (!skipExternalQuery
          ? externalSearchError ?? externalSearchCountError
          : undefined)
      : undefined;
  const combinedError =
    searchError ?? (!skipQueries ? searchCountError : undefined);

  useEffect(() => {
    if (combinedError) {
      setErrorLike(combinedError);
    }
  }, [combinedError]);

  useEffect(() => {
    if (externalQueryError) {
      console.error('External transaction search failed:', externalQueryError);
    }
  }, [externalQueryError]);

  const crmRows = useMemo<CrmManualSelectionRow[]>(() => {
    if (searchDataState !== 'complete') {
      return [];
    }
    const rows = searchData?.get_property_transactions_distance ?? [];
    return rows.map(row => ({ ...row, source: 'crm' as const }));
  }, [searchDataState, searchData?.get_property_transactions_distance]);

  const externalRows = useMemo<ExternalManualSelectionRow[]>(() => {
    if (skipExternalQuery || externalSourceAdapter == null) {
      return [];
    }
    const source = externalSourceAdapter.source;
    const rows = localExternalRows as ExternalSearchRow[];
    return rows.map(row => ({
      source,
      id: row.id,
      transaction_id: row.external_id,
      distance_in_meters: row.distance_in_meters ?? 0,
      transaction: {
        ...row,
        source,
      },
    }));
  }, [skipExternalQuery, externalSourceAdapter, localExternalRows]);

  const dedupedRows = useMemo(
    () => dedupeManualSelectionRows(crmRows, externalRows, existingKeys),
    [crmRows, externalRows, existingKeys],
  );

  const mergedRows = useMemo<MergedTransactionRow[]>(
    () =>
      mergeManualSelectionRows(
        dedupedRows.crmRows,
        dedupedRows.externalRows,
        orderBy as OrderByObject[],
      ),
    [dedupedRows, orderBy],
  );

  const settledMergedRowsRef = useRef<MergedTransactionRow[]>([]);
  useEffect(() => {
    if (!searchLoading && !externalSearchLoading) {
      settledMergedRowsRef.current = mergedRows;
    }
  }, [searchLoading, externalSearchLoading, mergedRows]);

  const displayedMergedRows =
    searchLoading || externalSearchLoading
      ? settledMergedRowsRef.current
      : mergedRows;

  const pagedRows = useMemo<MergedTransactionRow[]>(() => {
    if (!mergeWithExternal) {
      return dedupedRows.crmRows;
    }
    const pageSize = Math.max(limit, 1);
    return displayedMergedRows.slice(offset, offset + pageSize);
  }, [displayedMergedRows, dedupedRows, limit, offset, mergeWithExternal]);

  const toggleSelect = useCallback((id: string, source: TransactionSource) => {
    const key = encodeSelectionKey(source, id);
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      const newOffset = (page - 1) * Math.max(limit, 1);
      const next = new URLSearchParams(searchParams);
      next.set('offset', newOffset.toString());
      setSearchParams(next);
    },
    [searchParams, setSearchParams, limit],
  );

  const handleSave = useCallback(async () => {
    const newKeys = [...selectedIds].filter(k => !existingKeys.has(k));
    const removedKeys = [...existingKeys].filter(k => !selectedIds.has(k));

    if (newKeys.length === 0 && removedKeys.length === 0) {
      onClose();
      return;
    }

    const externalRowByKey = new Map(
      externalRows.map(row => [
        encodeSelectionKey(row.source, row.transaction.external_id),
        row,
      ]),
    );
    const crmRowByTransactionId = new Map(
      crmRows.map(r => [r.transaction_id, r]),
    );
    const existingByKey = new Map(
      existingTransactions.flatMap(tx => {
        const key = savedKey(tx);
        return key != null ? [[key, tx] as const] : [];
      }),
    );

    const removedRowIds = removedKeys
      .map(k => existingByKey.get(k)?.id)
      .filter((id): id is string => id != null);

    let nextOrderNr = Math.max(
      -1,
      ...existingTransactions.map(t => t.order_nr),
    );

    const inserts: Cma_Reports_Property_Transactions_Insert_Input[] = [];
    for (const key of newKeys) {
      const decoded = decodeSelectionKey(key);
      if (decoded == null) {
        continue;
      }
      if (decoded.source === 'crm') {
        const row = crmRowByTransactionId.get(decoded.id);
        inserts.push({
          cma_report_id: cmaReportId,
          source: 'crm',
          transaction_id: decoded.id,
          order_nr: (nextOrderNr += 1),
          distance_in_meters: row?.distance_in_meters ?? null,
        });
        continue;
      }
      inserts.push({
        cma_report_id: cmaReportId,
        source: decoded.source,
        external_id: decoded.id,
        order_nr: (nextOrderNr += 1),
        distance_in_meters:
          externalRowByKey.get(key)?.distance_in_meters ?? null,
        include_in_median: true,
      });
    }

    setErrorLike(null);

    try {
      await saveTransactions({
        variables: {
          cma_report_id: cmaReportId,
          transactions: inserts,
          crm_transaction_ids: inserts.flatMap(transaction =>
            transaction.source === 'crm' && transaction.transaction_id != null
              ? [transaction.transaction_id]
              : [],
          ),
          external_transaction_ids: inserts.flatMap(transaction =>
            transaction.source !== 'crm' && transaction.external_id != null
              ? [transaction.external_id]
              : [],
          ),
          to_delete_ids: removedRowIds,
        },
        refetchQueries: refetchTransactionsUnlessReturned(cmaReportId),
        awaitRefetchQueries: true,
      });

      const baseProperties = getBaseCmaEventProperties(me, cmaReportId);
      if (baseProperties) {
        track(ANALYTICS_EVENTS.CMA_TRANSACTIONS_MANUAL_SAVE, {
          ...baseProperties,
          count: newKeys.length,
          creation_source: creationSource,
        });
      }
      onClose();
    } catch (e) {
      console.error('Failed to save transactions:', e);
      setErrorLike(e as ErrorLike);
    }
  }, [
    existingTransactions,
    existingKeys,
    selectedIds,
    onClose,
    crmRows,
    externalRows,
    cmaReportId,
    saveTransactions,
    creationSource,
    track,
    me,
  ]);

  const refetchAll = useCallback(() => {
    const fetches: Promise<unknown>[] = [refetchSearch(), refetchSearchCount()];
    if (!skipExternalQuery) {
      fetches.push(refetchExternal());
    }
    return Promise.all(fetches);
  }, [refetchSearch, refetchSearchCount, refetchExternal, skipExternalQuery]);

  const externalTrueCount = mergeWithExternal
    ? dedupedRows.externalRows.length
    : 0;
  const trueTotalCount = crmCount + externalTrueCount;
  const reachableCount = mergeWithExternal
    ? displayedMergedRows.length
    : trueTotalCount;
  const pageCount = Math.ceil(reachableCount / Math.max(limit, 1));
  const selectionCount = selectedIds.size;
  const combinedLoading =
    !transactionsFragment.complete ||
    searchLoading ||
    externalSearchLoading ||
    externalSearchCountLoading;
  const showExternalQueryError =
    externalQueryError != null &&
    searchDataState === 'complete' &&
    !combinedLoading &&
    combinedError == null;

  return (
    <>
      <Dialog
        open={open}
        fullScreen
        sx={{
          '& .MuiDialog-paperFullScreen': {
            maxWidth: 'max(800px, calc(100vw - 156px))',
          },
        }}
        onClose={onClose}
        slots={{
          transition: SlideUpTransition,
        }}
      >
        <DialogContent
          sx={{
            padding: '0 !important',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <SelectionPickerHeader
            title={t('Select Transactions')}
            onClose={onClose}
            selectionCount={selectionCount}
            onClear={clearSelection}
            onSave={handleSave}
            saving={upsertingTransactions}
          />

          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: 'var(--mui-palette-background-default)',
            }}
          >
            <Box
              sx={{
                borderBottom: `1px solid ${
                  (theme.vars ?? theme).palette.divider
                }`,
              }}
            >
              <ListToolbar
                tableName="property_transactions_distance"
                quickFilters={quickFilters}
                queryParamsScope="transaction-selection"
                refetch={refetchAll}
                defaultFilterTypeExpanded="quick"
                viewLinks={[
                  { name: 'list', icon: Rows3 },
                  { name: 'grid', icon: LayoutGrid },
                ].map(({ name, icon: Icon }) => ({
                  name,
                  icon: (
                    <Icon
                      size={18}
                      strokeWidth={viewType === name ? 2.5 : 1.5}
                    />
                  ),
                }))}
              />
            </Box>

            {(combinedError || (!hasLocationFilter && hasWhereParam)) && (
              <Box sx={{ backgroundColor: 'background.paper', flexGrow: 1 }}>
                {combinedError && (
                  <Alert severity="error" sx={{ m: 2 }}>
                    {t('Could not load transactions')}
                  </Alert>
                )}
                {!hasLocationFilter && hasWhereParam && (
                  <Alert severity="info" sx={{ m: 2 }}>
                    {t(
                      'Please choose a location first to search for transactions',
                    )}
                  </Alert>
                )}
              </Box>
            )}

            {showExternalQueryError && (
              <Alert severity="info" sx={{ m: 2 }}>
                {t(
                  'DVF transactions could not be loaded. RealAdvisor transactions are still shown.',
                )}
              </Alert>
            )}

            {externalQueryError == null && externalGeometryUnavailable && (
              <Alert severity="info" sx={{ m: 2 }}>
                {t(
                  'DVF transactions are unavailable for this location. RealAdvisor transactions are still shown.',
                )}
              </Alert>
            )}

            {externalQueryError == null &&
              externalSourceAdapter != null &&
              (hasUnsupportedExternalFilters ||
                externalMultiLocalityUnavailable) && (
                <Alert severity="info" sx={{ m: 2 }}>
                  {t(
                    'DVF transactions are unavailable with the selected filters. RealAdvisor transactions are still shown.',
                  )}
                </Alert>
              )}

            {mergeWithExternal &&
              (externalResultsTruncated || crmResultsTruncated) && (
                <Alert severity="info" sx={{ m: 2 }}>
                  {t(
                    'Showing up to {{limit}} transactions per source. Refine your filters to see other transactions.',
                    { limit: EXTERNAL_TRANSACTIONS_MAX_RESULTS_PER_SOURCE },
                  )}
                </Alert>
              )}

            {hasLocationFilter && viewType === 'list' && (
              <Box
                sx={{
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                }}
              >
                <TransactionListView
                  transactions={pagedRows}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  loading={combinedLoading}
                  totalCount={reachableCount}
                  queryParamsScope="transaction-selection"
                />
              </Box>
            )}
            {hasLocationFilter && viewType === 'grid' && (
              <Box
                sx={{
                  flexGrow: 1,
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <TransactionGridView
                  transactions={pagedRows}
                  selectedIds={selectedIds}
                  onToggleSelect={toggleSelect}
                  loading={combinedLoading}
                />
              </Box>
            )}
          </Box>

          {viewType === 'grid' &&
            hasLocationFilter &&
            (trueTotalCount > 0 || combinedLoading) && (
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 0,
                  zIndex: 2,
                  backgroundColor: 'background.paper',
                  borderTop: `1px solid ${
                    (theme.vars ?? theme).palette.divider
                  }`,
                }}
              >
                <Toolbar
                  sx={{ display: 'flex', justifyContent: ['center', 'start'] }}
                >
                  {combinedLoading ? (
                    <Skeleton
                      variant="rectangular"
                      width={300}
                      height={36}
                      sx={{ borderRadius: 1 }}
                    />
                  ) : (
                    <Pagination
                      count={pageCount}
                      color="primary"
                      shape="rounded"
                      siblingCount={isMobile ? 0 : 1}
                      page={currentPage}
                      sx={{ justifyContent: 'center' }}
                      onChange={(_e, page) => handlePageChange(page)}
                      renderItem={item => (
                        <PaginationItem
                          slots={{
                            next: PaginationNextIcon,
                            previous: PaginationPreviousIcon,
                          }}
                          {...item}
                        />
                      )}
                    />
                  )}
                </Toolbar>
              </Box>
            )}
        </DialogContent>
      </Dialog>
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};

export default TransactionManualSelectionDialog;
